import { isCollateralContinueGenuinelySaved } from "../../../utils/helpers";

export interface StatusFlags {
  isApplicantAdditionalDetailsComplete: boolean;
  additionalCoApplicantCount: number;
  areAllCoApplicantsSaved: boolean;
  areAllCoApplicantsEmploymentComplete: boolean;
  isFundExpenseBalanced: boolean;
  enableContinue: boolean;
  isAdditionalSummaryComplete: boolean;
  isEntranceExamContinueDisabled: boolean | ((values: any, errors: any) => boolean);
  getFilledCountWithPanAadhaar: (fields: any[], values: any) => number;
  getFilledCount: (fields: any[], values: any) => number;
  areAllCoApplicantsVerified: (values: any) => boolean;
}

export const FUND_EXPENSE_AMOUNT_FIELDS = [
  "expenseTuitionFees",
  "expenseLivingExpenses",
  "expenseTravelExpenses",
  "expenseOtherExpenses",
  "fundRequestedLoanAmount",
  "fundOwnSources",
  "fundScholarshipAmount",
  "fundOtherIncome",
];

export interface AdditionalDetailsGateParams {
  activeVerticalIndex: number;
  values: Record<string, any>;
  errors: Record<string, any>;
  statusFlags: Pick<
    StatusFlags,
    | "isApplicantAdditionalDetailsComplete"
    | "additionalCoApplicantCount"
    | "areAllCoApplicantsSaved"
    | "areAllCoApplicantsEmploymentComplete"
    | "isFundExpenseBalanced"
    | "enableContinue"
    | "isAdditionalSummaryComplete"
  >;
  lastSavedCollateralSnapshotByGroup?: Record<number, string>;
  fetchUserApplicationData?: any;
  collateralHorizontalSteps?: any;
}

/**
 * Completeness gate for the Additional Details flow (horizontal 3, verticals 0-4).
 *
 * These checks used to sit in isNavigationDisabled, which left Continue greyed
 * out with nothing explaining why. They now run when Continue is CLICKED, so the
 * user gets told what is missing instead. isNavigationDisabled keeps only the
 * mechanical guards for that section (submit in flight / pincode lookup).
 *
 * @returns null when the step may proceed, otherwise a user-facing reason.
 */
export function getAdditionalDetailsBlockReason({
  activeVerticalIndex,
  values,
  errors,
  statusFlags: {
    isApplicantAdditionalDetailsComplete,
    additionalCoApplicantCount,
    areAllCoApplicantsSaved,
    areAllCoApplicantsEmploymentComplete,
    isFundExpenseBalanced,
    enableContinue,
    isAdditionalSummaryComplete,
  },
  lastSavedCollateralSnapshotByGroup,
  fetchUserApplicationData,
  collateralHorizontalSteps,
}: AdditionalDetailsGateParams): string | null {
  switch (activeVerticalIndex) {
    // Applicant Additional Details
    case 0:
      return isApplicantAdditionalDetailsComplete
        ? null
        : "Please fill in all the required details correctly before proceeding.";

    // Co-Applicant Additional Details
    case 1: {
      // The saved/complete flags only reflect the state at the last Save — if a
      // co-applicant field is edited afterwards into something invalid (e.g.
      // Years in Current Address/City > 150), this must still block.
      const hasCoApplicantAddressErrors =
        errors &&
        Object.keys(errors).some(
          (key) =>
            /^(basicCoApplicant|addressCoApplicant|employmentCoApplicant)\d+/.test(
              key
            ) && Boolean(errors[key])
        );

      if (additionalCoApplicantCount < 2) {
        return "Please add the required 2 co-applicant details before proceeding.";
      }
      if (!areAllCoApplicantsSaved) {
        return "Please save each co-applicant's details before proceeding.";
      }
      if (!areAllCoApplicantsEmploymentComplete) {
        return "Please complete each co-applicant's employment details before proceeding.";
      }
      if (hasCoApplicantAddressErrors) {
        return "Please correct the highlighted co-applicant details before proceeding.";
      }
      return null;
    }

    // Fund & Expense
    case 2: {
      const hasFundExpenseErrors =
        errors &&
        FUND_EXPENSE_AMOUNT_FIELDS.some((name) => Boolean(errors[name]));
      if (hasFundExpenseErrors) {
        return "Please fill in all the required details correctly before proceeding.";
      }
      return isFundExpenseBalanced
        ? null
        : "Total funds must match total expenses before proceeding.";
    }

    // Reference & Collateral
    case 3: {
      const rawHasCollateral = values?.hasCollateral;
      const hasCollateralAnswer = String(
        rawHasCollateral && typeof rawHasCollateral === "object"
          ? (rawHasCollateral as any).value ?? (rawHasCollateral as any).label
          : rawHasCollateral ?? ""
      )
        .trim()
        .toLowerCase();

      if (hasCollateralAnswer === "yes") {
        return isCollateralContinueGenuinelySaved(
          values,
          lastSavedCollateralSnapshotByGroup,
          fetchUserApplicationData,
          collateralHorizontalSteps
        )
          ? null
          : "Please save the collateral details before proceeding.";
      }
      // "No" has no dedicated Save & Next — Continue itself persists the answer.
      if (hasCollateralAnswer === "no") return null;
      return enableContinue
        ? null
        : "Please complete the reference and collateral details before proceeding.";
    }

    // Additional Details Summary
    case 4:
      return isAdditionalSummaryComplete
        ? null
        : "Please complete all the sections before proceeding.";

    default:
      return null;
  }
}

export interface NavigationDisabledParams {
  activeHorizontalIndex: number;
  activeVerticalIndex: number;
  isSubmitting: boolean;
  isSubmittingToAPI: boolean;
  zipCodeLoading: boolean;
  isMobileVerified: boolean;
  isEmailVerified: boolean;
  isLoadTesting: boolean;
  values: Record<string, any>;
  errors: Record<string, any>;
  fields: Array<{ name: string; required?: boolean; [key: string]: any }>;
  otherFields: Array<{ name: string; required?: boolean; [key: string]: any }>;
  currFields: any[];
  companyFields: any[];
  isDocumentVerified: any;
  statusFlags: StatusFlags;
  // Collateral step only — passed through to isCollateralContinueGenuinelySaved
  // so the "yes, genuinely saved" check runs fresh off live `values` on every
  // render here, instead of trusting statusFlags.enableContinue alone (that
  // latch is only refreshed by DynamicForm's own reconcile effect, which is
  // gated to specific dependency changes and can lag a keystroke behind —
  // e.g. typing a Collateral Value right after answering "Yes" used to read
  // as "genuinely saved" for one render because enableContinue hadn't been
  // re-evaluated yet).
  lastSavedCollateralSnapshotByGroup?: Record<number, string>;
  fetchUserApplicationData?: any;
  collateralHorizontalSteps?: any;
  // Reference & Collateral step only — which of its two tabs (0 = Reference,
  // 1 = Collateral) is currently active, so Continue from the Reference tab
  // isn't blocked by collateral requirements it hasn't reached yet.
  referenceCollateralActiveTab?: number;
}

const checkTwoRef = (obj: any) => {
  if(Object.keys(obj).length > 0) {
      if("basicReference1Email" in obj && "basicReference2Email" && obj["basicReference1Email"] && obj["basicReference2Email"]) {
        return false
      }
  }
  return true;
}

/**
 * Determines whether the form navigation or submission should be disabled.
 * 
 * @returns True if the "Continue" or action button should be disabled
 */
export function isNavigationDisabled({
  activeHorizontalIndex,
  activeVerticalIndex,
  isSubmitting,
  isSubmittingToAPI,
  zipCodeLoading,
  isMobileVerified,
  isEmailVerified,
  isLoadTesting,
  values,
  errors,
  fields,
  otherFields,
  currFields,
  companyFields,
  isDocumentVerified,
  lastSavedCollateralSnapshotByGroup,
  fetchUserApplicationData,
  collateralHorizontalSteps,
  referenceCollateralActiveTab,
  statusFlags: {
    isApplicantAdditionalDetailsComplete,
    additionalCoApplicantCount,
    areAllCoApplicantsSaved,
    areAllCoApplicantsEmploymentComplete,
    isFundExpenseBalanced,
    enableContinue,
    isAdditionalSummaryComplete,
    isEntranceExamContinueDisabled,
    getFilledCountWithPanAadhaar,
    getFilledCount,
    areAllCoApplicantsVerified
  },
}: NavigationDisabledParams): boolean {
  // --- LAYER 1: HORIZONTAL INDEX MULTIPLEXING ---

  
  // Case A: Section 3 (Additional Workflows)
  if (activeHorizontalIndex === 3) {
    // Covers Reference & Collateral / Co-Applicant Additional Details, which
    // this branch handles directly and never falls through to the
    // zipCodeLoading check further below — without this, Continue stayed
    // enabled here while a pincode lookup (getZipCode/getPinCode) was still
    // in flight for those steps.
    if (isSubmitting || isSubmittingToAPI || zipCodeLoading) return true;

    // Only the mechanical guards above disable Continue here now. The
    // per-vertical completeness checks (verticals 0-4) moved to
    // getAdditionalDetailsBlockReason, which the Continue handler runs on CLICK
    // so the user is told what is missing instead of facing a dead button.
    return false;
  }
  
  // --- LAYER 2: STANDARD WORKFLOW CRITERIA ---
  // Applies to activeHorizontalIndex 0, 1 and 2

  // Mobile and email verification are required before leaving the first
  // onboarding page. Aadhaar verification is handled on horizontal step 1.
  // if (activeHorizontalIndex === 0 && activeVerticalIndex === 0) {
  //   if (!isMobileVerified || !isEmailVerified) {
  //     return true;
  //   }
  // }

  // if(activeHorizontalIndex === 0 && activeVerticalIndex === 1) {
  //   const filteredOtherFields = otherFields.filter((f) => {
  //     const isUniversity = f.name === "university";
  //     const isInstitute = f.name === "institute";
  //     const isCourseName = f.name === "courseName";
  //     const applyAdmissionUndecided = values?.admissionStatus === "Undecided";
  //     const exclude = applyAdmissionUndecided && (isUniversity || isInstitute || isCourseName);
  //     return !exclude;
  //   });
  //   return getFilledCountWithPanAadhaar(filteredOtherFields, values) !== filteredOtherFields.length;
  // }

  // Base structural verification blocks
  // if (zipCodeLoading || isSubmitting || isSubmittingToAPI) {
  //   return true;
  // }

  // Mobile/Email/Aadhaar verification is only captured on the Student Details
  // step (horizontalIndex 1, verticalIndex 0), so it should only gate Continue
  // there — not on every later step, where it should re-enable once that
  // step's own fields are filled in.
  // if (activeHorizontalIndex === 1 && activeVerticalIndex === 0) {
  //   if (!isMobileVerified || !isEmailVerified || !isDocumentVerified) {
  //     return true;
  //   }

  //   // Verification engine for structural identity matching (Skipped in load tests)
  //   if (!isLoadTesting) {
  //     const isAadhaarVerified = values?.isAadhaarVerified;
  //     if (isAadhaarVerified !== true && isAadhaarVerified !== "true") {
  //       return true;
  //     }
  //   }
  // }

  // Validation block for relationship structures
  if (!areAllCoApplicantsVerified(values)) {
    return true;
  }
  // if(activeHorizontalIndex === 1 && activeVerticalIndex === 1) {
  //   const requiredCurrentAddressFields = [
  //     "currentOwnershipStatus",
  //     "currentYearsInCurrentAddress",
  //     "currentYearsInCurrentCity",
  //     "currentAddressLine1",
  //     "currentPincode"
  //   ];
  //   const requiredAddressFields = values && Object.keys(values)?.filter((f) => {
  //     return requiredCurrentAddressFields.includes(f) && !values[f];
  //   });
  //   // Blank-check alone misses fields that are filled but fail validation
  //   // (e.g. Years in Current Address/City > 150), so also block Continue
  //   // whenever Formik has a live error for any permanent/current address field.
  //   const hasAddressErrors = errors && Object.keys(errors).some(
  //     (key) => /^(permanent|current)/.test(key) && Boolean(errors[key])
  //   );
  //   return (requiredAddressFields?.length > 0) || hasAddressErrors;
  // }

  // Case C: Section 1 & Vertical Step 2 (Employment Rules Matrix)
  // if (activeHorizontalIndex === 1 && activeVerticalIndex === 2) {
  //   if (values?.employmentisEmployed === "No") {
  //     return !values?.employmentisEmployed;
  //   }

  //   // Dynamic analysis for active employment categories ("Others", "Farmer", etc.)
  //   const allEmploymentFields = fields.filter((f) => f.name !== "employmentisEmployed");
  //   const shouldShowOnlyRequired = values?.employmentType === "Others" || values?.employmentType === "Farmer";
    
  //   const employmentFieldsToCheck = shouldShowOnlyRequired
  //     ? allEmploymentFields.filter((f) => f.required && f.name !== "employmentYear" && f.name !== "employmentMonth" && f.name !== "employmentDesignation")
  //     : allEmploymentFields;

  //   const isEmpFieldsIncomplete = getFilledCountWithPanAadhaar(employmentFieldsToCheck, values) !== employmentFieldsToCheck.length;
  //   const isWorkplaceIncomplete = getFilledCount(currFields, values) !== currFields.length || getFilledCount(companyFields, values) !== companyFields.length;

  //   return isEmpFieldsIncomplete || isWorkplaceIncomplete;
  // }

  // Case D: Section 1 & Vertical Step 4 (Examination Sub-Workflow)
  // if (activeHorizontalIndex === 1 && activeVerticalIndex === 4) {
  //   return typeof isEntranceExamContinueDisabled === 'function'
  //     ? isEntranceExamContinueDisabled(values, errors)
  //     : !!isEntranceExamContinueDisabled;
  // }

  // Case E: Section 1 & Vertical Step 5 (CoApplicant Details Section)
  // Filled-count alone misses fields that are filled but fail validation
  // (e.g. Years in Current Address/City > 150), so also block Continue
  // whenever Formik has a live error for any co-applicant field.
  // if (activeHorizontalIndex === 1 && activeVerticalIndex === 5) {
  //   const isIncomplete = getFilledCountWithPanAadhaar(otherFields, values) !== otherFields.length;
  //   const hasCoApplicantErrors = errors && Object.keys(errors).some(
  //     (key) => /^(basicCoApplicant|addressCoApplicant|employmentCoApplicant)\d+/.test(key) && Boolean(errors[key])
  //   );
  //   return isIncomplete || hasCoApplicantErrors;
  // }

  // --- LAYER 3: FALLBACK DYNAMIC STRUCTURAL MATCHING ---
  // Runs if no custom vertical step logic intercept covers the sequence
  const filteredOtherFields = otherFields.filter((f) => {
    const applyAdmissionUndecided = activeHorizontalIndex === 0 && activeVerticalIndex === 1 && values?.admissionStatus === "Undecided";
    const isExcludedField = f.name === "university" || f.name === "institute" || f.name === "courseName";
    
    return !(applyAdmissionUndecided && isExcludedField);
  });

  if(activeHorizontalIndex <= 1) {
    return false
  }

  return getFilledCountWithPanAadhaar(filteredOtherFields, values) !== filteredOtherFields.length;
}

// Interface representing an individual field object in the API response
interface ApiField {
  name?: string;
  value?: string | number | boolean | null;
  [key: string]: unknown;
}

// Interface representing the structural hierarchy of the API response


// Interface for the aggregated key-value object
type CombinedFields = Record<string, string | number | boolean | null | undefined>;

export function validateCoApplicantFields(apiResponse: any): any {
  
  const getFieldsFromHorizontalStep = (horizontalIndex: number): any[] => {
    const verticalStepsArray: any[] = 
      apiResponse?.horizontalSteps?.[horizontalIndex]?.verticalSteps || [];

    // Search verticalSteps array for steps named "co-applicant" OR "loandetails"
    const targetStep = verticalStepsArray.find((step: any) => {
      const stepName = step?.verticalStepName?.toLowerCase() || '';
      return (
        stepName.includes('co-applicant aditional details') ||
        stepName.includes('coapplicant') ||
        stepName.includes('loandetails') ||
        stepName.includes('co-applicant details')
      );
    });

    return targetStep?.fields || [];
  };

  // Step 1: Extract fields dynamically for horizontalSteps[1] and horizontalSteps[3]
  const h1v5Fields = getFieldsFromHorizontalStep(1);
  const h3v1Fields = getFieldsFromHorizontalStep(3);
  // Step 2: Combine all fields into a single key-value object
  const combinedFields: CombinedFields = [...h1v5Fields, ...h3v1Fields].reduce<CombinedFields>(
    (acc, field) => {
      if (field && typeof field === 'object') {
        // Option A: If the object has a "key" and "value" property
        if ('key' in field && field.key) {
          acc[field.key] = field.value;
        } 
        // Option B: If the object has dynamic keys like { basicCoApplicant1RelationWithStudent: "Brother" }
        else {
          Object.entries(field).forEach(([k, v]) => {
            acc[k] = v as string | number | boolean | null | undefined;
          });
        }
      }
      return acc;
    },
    {}
  );

  // Step 3: Identify co-applicant numbers (e.g., 1, 2, 3)
  const coApplicantIndices = new Set<string>();
  const pattern = /^(?:basic|address|employment)CoApplicant(\d+)/;

  Object.keys(combinedFields).forEach((key) => {
    const match = key.match(pattern);
    if (match && match[1]) {
      coApplicantIndices.add(match[1]);
    }
  });

  // If no co-applicants exist, return false
  console.log(coApplicantIndices,"coApplicantIndices")
  if (coApplicantIndices.size === 0) {
    return {status:false,count:coApplicantIndices.size};
  }

  // Helper to check if a value is present and non-empty
  const hasValue = (val: unknown): boolean =>
    val !== undefined && val !== null && String(val).trim() !== '';

  // Step 4: Validate the 3 required fields for every co-applicant present
  const indicesList = Array.from(coApplicantIndices);

  for (const index of indicesList) {
    const relationKey = `basicCoApplicant${index}RelationWithStudent`;
    const ownershipKey = `addressCoApplicant${index}permanentOwnershipStatus`;
    const employmentKey = `employmentCoApplicant${index}isEmployed`;

    const isValid =
      hasValue(combinedFields[relationKey]) &&
      hasValue(combinedFields[ownershipKey]) &&
      hasValue(combinedFields[employmentKey]);

    if (!isValid) {
      return {status:false,count:coApplicantIndices.size};
    }
  }

 return {status:true,count:coApplicantIndices.size};
}

// reusable from wherever Basic Details gets saved independently of
// Employment.
export const hasCoApplicantPayPartyChanged = (
  coAppNum: string | number,
  currentValues: Record<string, any> | undefined | null,
  previousValues: Record<string, any> | undefined | null,
): boolean => {
  const fieldName = `basicCoApplicant${coAppNum}PayParty`;
  const resolvePayParty = (raw: any): string => {
    const resolved = raw && typeof raw === "object" ? raw.value ?? raw.label : raw;
    return String(resolved ?? "").trim().toLowerCase();
  };
  return (
    resolvePayParty(currentValues?.[fieldName]) !==
    resolvePayParty(previousValues?.[fieldName])
  );
}

// gets to check it, since those track live edits.
export const getFetchedCoApplicantPayParty = (
  fetchUserApplicationData: any,
  coAppNum: string | number,
): any => {
  const steps = fetchUserApplicationData?.horizontalSteps;
  if (!Array.isArray(steps)) return undefined;

  const fieldName = `basicCoApplicant${coAppNum}PayParty`;

  const applicantLoanStep = steps.find(
    (step: any) => step?.horizontalStepName === "Applicant & Loan Details",
  );
  const documentsStep = steps.find(
    (step: any) =>
      step?.horizontalStepName === "Additional Details & Documents",
  );
  const loanDetailsVStep = applicantLoanStep?.verticalSteps?.find(
    (vStep: any) => vStep?.verticalStepName === "Co-Applicant Details",
  );
  const coApplicantAdditionalDetailsVStep = documentsStep?.verticalSteps?.find(
    (vStep: any) =>
      vStep?.verticalStepName === "Co-Applicant Additional Details" ||
      vStep?.verticalStepName === "Co-Applicant Aditional Details",
  );

  const findInVStep = (vStep: any): any => {
    if (!Array.isArray(vStep?.fields)) return undefined;
    for (const fieldObj of vStep.fields) {
      if (fieldObj && typeof fieldObj === "object" && fieldName in fieldObj) {
        return fieldObj[fieldName];
      }
    }
    return undefined;
  };

  const fromLoanDetails = findInVStep(loanDetailsVStep);
  if (fromLoanDetails !== undefined) return fromLoanDetails;
  return findInVStep(coApplicantAdditionalDetailsVStep);
};

// Flattens every saved field of ONE co-applicant (basic/address/employment,
// across all horizontal/vertical steps) out of the raw fetch-application-data
// response into a plain { fieldName: value } map. Summary pages only mount
// the tab currently being edited, so e.g. a Basic Details save has no
// employment values in Formik — this is the fallback source for those.
export const getSavedCoApplicantFieldValues = (
  fetchUserApplicationData: any,
  coAppNum: string | number,
): Record<string, any> => {
  const result: Record<string, any> = {};
  const steps = fetchUserApplicationData?.horizontalSteps;
  if (!Array.isArray(steps)) return result;

  const belongsToCoApp = (name: string) =>
    new RegExp(`CoApplicant${coAppNum}(?!\d)`, "i").test(name);
  const unwrap = (raw: any) =>
    raw && typeof raw === "object" && !Array.isArray(raw) && "value" in raw
      ? raw.value
      : raw;
  const isFilled = (val: any) => val !== undefined && val !== null && val !== "";

  steps.forEach((step: any) => {
    (step?.verticalSteps || []).forEach((vStep: any) => {
      (Array.isArray(vStep?.fields) ? vStep.fields : []).forEach((fieldObj: any) => {
        if (!fieldObj || typeof fieldObj !== "object") return;
        const name = String(fieldObj?.name || "");
        if (name && belongsToCoApp(name)) {
          const val = unwrap(
            fieldObj[name] !== undefined ? fieldObj[name] : fieldObj.value,
          );
          if (isFilled(val) && !isFilled(result[name])) result[name] = val;
        }
        Object.keys(fieldObj).forEach((key) => {
          if (!belongsToCoApp(key)) return;
          const val = unwrap(fieldObj[key]);
          if (isFilled(val) && !isFilled(result[key])) result[key] = val;
        });
      });
    });
  });
  return result;
};

export function computeEmploymentFillStatusForCoApplicants(
  values: any,
  coApplicantEvaluationFields: any[],
  coApplicantNums: Set<string>,
): { coAppKey: string; isEmploymentFilled: boolean }[] {
  return Array.from(coApplicantNums).map((num) => {
    const coApplicantFields = coApplicantEvaluationFields.filter((f: any) => {
      const name = String(f?.name || "");
      return new RegExp(`^(?:basic|address|employment)CoApplicant${num}`).test(
        name,
      );
    });

    const relevantRequiredFields = coApplicantFields.filter((f: any) => {
      const fieldName = String(f?.name || "");
      if (f?.required === false) return false;
      if (f?.type === "hidden") return false;
      if (/ScoreType$/.test(fieldName)) return false;
      if (
        /^basicCoApplicant\d+(AadhaarVerified|IsMobileVerified|IsEmailVerified|AadhaarNumber)$/.test(
          fieldName,
        )
      ) {
        return false;
      }

      const empMatch = fieldName.match(/^employmentCoApplicant(\d+)(.+)$/);
      const empField = (n: string | number, suffix: string) =>
        `employmentCoApplicant${n}${suffix}`;

      if (empMatch) {
        const employmentTypeFieldName = empField(empMatch[1], "employmentType");
        const coAppNum = empMatch[1];
        const suffix = empMatch[2];
        const isEmployedFieldName =
          coApplicantFields.find((fld: any) =>
            new RegExp(
              `^employmentCoApplicant${coAppNum}(employmentisEmployed|isEmployed)$`,
            ).test(String(fld?.name || "")),
          )?.name || `employmentCoApplicant${coAppNum}employmentisEmployed`;
        const isEmployedValue = values?.[isEmployedFieldName];

        if (
          isEmployedValue === "Yes" &&
          (values?.[employmentTypeFieldName] === "Others" ||
            values?.[employmentTypeFieldName] === "Farmer") &&
          fieldName !== employmentTypeFieldName &&
          fieldName !== empField(coAppNum, "monthlyObligation") &&
          fieldName !== empField(coAppNum, "monthlyIncome")
        ) {
          return false;
        }
        if (suffix === "employmentisEmployed" || suffix === "isEmployed") {
          return true;
        }
        if (isEmployedValue === "No") return false;
      }

      return true;
    });

    const filledRelevantFields = relevantRequiredFields.filter((f: any) => {
      const value = values?.[f.name];
      if (value === null || value === undefined) return false;
      if (typeof value === "string") return value.trim() !== "";
      if (typeof value === "boolean") return value;
      if (typeof value === "number") return !Number.isNaN(value);
      if (typeof value === "object") {
        if ("value" in value || "label" in value) {
          return String(value?.value ?? value?.label ?? "").trim() !== "";
        }
        return Object.keys(value).length > 0;
      }
      return true;
    }).length;

    const isEmploymentFilled =
      relevantRequiredFields.length > 0 &&
      filledRelevantFields === relevantRequiredFields.length;

    return { coAppKey: `CoApplicant${num}`, isEmploymentFilled };
  });
}
